<template>
  <Print v-if="isPrint" :form="form" :board="board" />
  <div v-else>
    <!-- 기본정보 -->
    <field-select-module v-model="form.stdInfo" :board="this.board" :is-print="isPrint" :multiple-disaster="true" />

    <!-- 작업구분 -->
    <div>
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot"></i> 작업구분</h5>
        </vs-col>
      </vs-row>

      <div class="gap-x-6 gap-y-4 mt-4" :class="{ 'grid grid-cols-12': !isMobile }">
        <div v-for="checkbox in form.inspectionList" :key="checkbox.id" class="items-center" :class="{ 'flex col-span-3' : !isMobile, 'mt-1' : isMobile }">
          <PrintCheckbox v-if="isPrint || board.readonly" :label="checkbox.title" :value="checkbox.check" color />
          <label v-else class="flex">
            <vs-checkbox :readonly="board.readonly" v-model="checkbox.check" @change="checkForm"> </vs-checkbox>
            {{ checkbox.title }}
          </label>
        </div>
      </div>
    </div>

    <!--작업허가서 정보-->
    <div class="mt-6">
      <vs-row class="my-4">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot"></i> 작업허가서 정보</h5>
        </vs-col>
      </vs-row>
      <div class="writeGridContainer mt-4">
        <div>
          <div>작업일자</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_1 }}</p>
          <date-selector class="w-full" v-model="form.inputB_1" :readonly="board.readonly" />
        </div>
        <div>
          <div>협력업체명</div>
          <span v-if="isPrint" class="print-input"> {{ form.companyName }} </span>
          <CompanySelector v-else :readonly="board.readonly" v-model="form.inputB_2" placeholder="선택" @select="targetCompany"></CompanySelector>
        </div>
        <div>
          <div>허가번호</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_3 }}</p>
          <ml-input :readonly="board.readonly" v-else v-model="form.inputB_3" />
        </div>

        <div>
          <div>관리감독자</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_4 }}</p>
          <ml-input :readonly="board.readonly" v-else v-model="form.inputB_4" />
        </div>
        <div>
          <div>작업내용</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_5 }}</p>
          <ml-input :readonly="board.readonly" v-else v-model="form.inputB_5" />
        </div>
        <div>
          <div>작업장소</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_6 }}</p>
          <ml-input :readonly="board.readonly" v-else v-model="form.inputB_6" />
        </div>

        <div>
          <div>주간작업</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_7 }} ~ {{ form.inputB_8 }}</p>
          <div v-else class="time">
            <time-selector v-model="form.inputB_7" :readonly="board.readonly" />
            <p class="px-2">~</p>
            <time-selector v-model="form.inputB_8" :readonly="board.readonly" />
          </div>
        </div>
        <div>
          <div>야간연장<br />(18:00 이후)</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_9 }} ~ {{ form.inputB_10 }}</p>
          <div v-else class="time">
            <time-selector v-model="form.inputB_9" :readonly="board.readonly" />
            <p class="px-2">~</p>
            <time-selector v-model="form.inputB_10" :readonly="board.readonly" />
          </div>
        </div>
        <div></div>

        <div>
          <div>주간인원</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_11 }}</p>
          <comma-input :readonly="board.readonly" v-else v-model="form.inputB_11" />
        </div>
        <div>
          <div>야간인원</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_12 }}</p>
          <comma-input :readonly="board.readonly" v-else v-model="form.inputB_12" />
        </div>
      </div>
    </div>

    <!-- 목록표 -->
    <div v-for="(inspection, indexRows) in form.inspectionList" :key="indexRows" v-show="inspection.check">
      <TableInspectMerge
        :is-print="isPrint"
        :is-readonly="board.readonly"
        :title="inspection.title"
        tableHeaderTitle="작업전 안전조치 및 확인 점검 사항(이행여부 = V, 해당사항없음 = N/A)"
        :tableHeaderCheck="[{ text: '양호' }, { text: '불량' }, { text: 'N/A' }]"
        :isNote="false"
        v-model="inspection.data"
        class="mt-28px"
      />

      <!-- 가스농도 측정 -->
      <table-custom
        v-if="indexRows === 0"
        :controls="true"
        :multiple="true"
        :board="board"
        :is-print="isPrint"
        title="가스농도 측정"
        :tableData="[
          { id: 'input1', title: '물질명', type: 'text', placeholder: '' },
          { id: 'input2', title: '결과', type: 'text', placeholder: '' },
          { id: 'input3', title: '측정시간', type: 'time', placeholder: '' },
          { id: 'input4', title: '측정자/확인자', type: 'text', placeholder: '' },
        ]"
        :mobile="['input1', 'input2']"
        v-model="form.gas"
        class="mt-4"
      />
      <p v-if="indexRows === 0">※(가스농도 측정결과 ①HC:0% ②O2:18%이상 ③CO:30ppm미만 ④CO2:1.5%미만 ⑤H2S:10ppm미만)</p>
    </div>

    <!-- 기타특별사항 -->
    <div class="mt-6">
      <vs-row class="my-4">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot"></i> 기타특별사항</h5>
        </vs-col>
      </vs-row>
      <div class="mt-4">
        <p v-if="isPrint" class="print-textarea">{{ form.inputB_3 }}</p>
        <ml-textarea :readonly="board.readonly" v-else v-model="form.inputB_3" height="140px" />
      </div>
    </div>

    <!-- 작업허가(추가연장) -->
    <table-custom
      :controls="true"
      :multiple="true"
      :board="board"
      :is-print="isPrint"
      title="작업허가(추가연장)"
      :tableData="[
        { id: 'input1', title: '작업일', type: 'date', placeholder: '' },
        { id: 'input2', title: '작업시간', type: 'timeArr', placeholder: '' },
        { id: 'input3', title: '작성자', type: 'text', placeholder: '' },
        { id: 'input4', title: '검토자', type: 'text', placeholder: '' },
        { id: 'input5', title: '승인자', type: 'text', placeholder: '' },
      ]"
      :mobile="['input2']"
      v-model="form.extension"
      class="mt-4"
    />

    <div class="flex flex-row justify-between items-center" style="background: #f9f9f9; border: 1px solid #cdcdcd">
      <p class="p-4">작업허가 요청 /연장 신청 시 상기 안전조치 사항을 이행하였음을 서명합니다.</p>
      <div class="flex flex-row items-center gap-4 p-4" style="border-left: 1px solid #cdcdcd">
        <sign-field v-model="form.sign3.sign" :readonly="board.readonly" class="bg-white" />
        <div>(인)</div>
      </div>
    </div>

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import DateSelector from "@/components/selector/DateSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import FileUploadModule from "@/views/modules/FileUploadModule";
import TableInspectMerge from "@/components/TableInspectMerge";
import TableCustom from "@/components/TableCustom";
import TimeSelector from "@/components/selector/TimeSelector.vue";
import Print from "./Print.vue";
import SignField from "@/components/SignField.vue";
import { helpers, required } from "vuelidate/lib/validators";
import inspectData from "./inspectData";
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";

export default {
  components: {
    Print,
    TimeSelector,
    TableInspectMerge,
    FieldSelectModule,
    FileUploadModule,
    CompanySelector,
    DateSelector,
    SignField,
    PrintCheckbox,
    TableCustom,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  validations: {},
  data() {
    return {
      isMobile: false,

      form: {
        stdInfo: {},
        inspectionList: JSON.parse(JSON.stringify(inspectData)),
        typeTitleList: [],

        inputB_1: "",
        inputB_2: "",
        inputB_3: "",
        inputB_4: "",
        inputB_5: "",
        inputB_6: "",
        inputB_7: "",
        inputB_8: "",
        inputB_9: "",
        inputB_10: "",
        inputB_11: "",
        inputB_12: "",

        sign1: {
          name: "",
          sign: "",
        },
        sign2: {
          name: "",
          sign: "",
        },
        sign3: {
          name: "",
          sign: "",
        },

        gas: [],
        extension: [],

        fileList: [],
        companyName: "",
      },
      signlist: ["1. 신청자(협력사 소장)", "2. 검토자 (네트웍스 관리감독자)", "3. 검토자 (네트웍스 안전관리자)", "4. 승인자 (네트웍스 현장대리인)"],
    };
  },
  watch: {
    value: {
      important: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  created() {
    const keys = Object.keys(this.form);
    for (const key of keys) {
      if (this.value[key]) {
        this.form[key] = this.value[key];
      }
    }

    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  methods: {
    validate() {},
    checkForm() {
      this.form.typeTitleList = this.form.inspectionList.filter((li) => li.check).map((il) => il.title);
    },
    updateListCheck(indexList, indexTableRows, indexRows, text) {
      this.form.inspectionList[indexList].list[indexTableRows].list[indexRows].is_checked = text;
    },
    targetCompany(value) {
      this.form.companyName = value.com_name;
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
  },
};
</script>
